import { useRef } from 'preact/hooks';

import { Text } from '../UNSAFE_Text';

import { useNavigationListItem } from './useNavigationListItem';
import { Flex } from '../UNSAFE_Flex';
import { Badge } from '../UNSAFE_Badge';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { WarningS } from '../UNSAFE_RedwoodIcons/WarningS';
import { ErrorS } from '../UNSAFE_RedwoodIcons/ErrorS';
import { InformationS } from '../UNSAFE_RedwoodIcons/InformationS';
import { SuccessS } from '../UNSAFE_RedwoodIcons/SuccessS';
import {
  NavigationListItemStyles,
  NavigationListItemVariantOptions
} from '../UNSAFE_NavigationList/themes/NavigationListItemStyles.css';
import { NavigationListItemRedwoodTheme } from './themes/redwood/NavigationListItemTheme';
import { ComponentChild, ComponentChildren } from 'preact';
import { useTabBarItemTooltip } from '#UNSAFE_TabBarCommon/useTabBarItemTooltip';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

type Severity = 'warning' | 'info' | 'none' | 'error' | 'confirmation';
type AriaProps = {
  /**
   * Accepts the panel element's ID associated with the item
   **/
  'aria-controls'?: string;
};

export type BaseNavigationListItemProps<K extends string | number> = {
  /**
   * Key of the NavigationListItem.
   */
  itemKey: K;
  /**
   * Label of the NavigationListItem.
   */
  label: string;
  /**
   * The content to be rendered inside the Badge component.
   */
  badge?: string;
  /**
   * The content to be rendered inside the Text component as a metadata.
   */
  metadata?: string;
  /**
   * The status icon to be rendered after the label.
   */
  severity?: Severity;
  /**
   * The content to be rendered for remove icon
   */
  removeIcon?: ComponentChildren;
  /**
   * The icon before the label or the stand alone icon
   */
  icon?: ComponentChild;
  /**
   * Sets the URL that the hyperlink points to. If there is no valid URL use "#" for href value to navigate to the top of the page.
   */
  href?: string;
} & AriaProps;

const onClickLink = (event: MouseEvent) => {
  event.preventDefault();
};
/*TODO: JET-58535. Add ripple effect when touching navigationListItem. */
export function BaseNavigationListItem<K extends string | number>({
  itemKey,
  label,
  badge,
  metadata,
  severity = 'none',
  removeIcon,
  icon,
  href,
  'aria-controls': ariaControls
}: BaseNavigationListItemProps<K>) {
  const itemRef = useRef<HTMLDivElement | null>(null);

  //Logic of the component handled here
  const {
    itemId,
    itemHandlers,
    itemClasses,
    isSelected,
    labelContainerClasses,
    labelContainerStyle,
    isIconOnly,
    isEdgeEnd,
    stackedBadgeClasses,
    iconOnlyClasses,
    isCurrent,
    showFocusRing
  } = useNavigationListItem({
    itemKey
  });

  const severityRequirement = !(severity === 'none' || severity === undefined);

  const IconComponent = (severityValue: Severity) => {
    switch (severityValue) {
      case 'error':
        return <ErrorS color="danger" />;
      case 'warning':
        return <WarningS color="warning" />;
      case 'info':
        return <InformationS color="info" />;
      case 'confirmation':
        return <SuccessS color="success" />;
      default:
        return null;
    }
  };

  const {
    styles: {
      iconLabelContainer,
      iconContainer,
      startIconPadding,
      endContentPadding,
      removeIconPaddingWithEndContent,
      navigationItemMetadata,
      navigationListItemLink
    }
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme
  );

  const navigationItemMatadata = classNames([navigationItemMetadata, endContentPadding]);
  const navigationItemLinkClass = classNames([navigationListItemLink]);

  const endIconContainerClasses = classNames([
    iconLabelContainer,
    iconContainer,
    endContentPadding
  ]);
  const startIconContainerClasses = classNames([
    !isIconOnly && iconLabelContainer,
    iconContainer,
    //For icon only icon to label padding is not required.
    !isIconOnly && startIconPadding,
    isIconOnly && iconOnlyClasses
  ]);
  const {
    styles: { navigationlistRemoveIcon }
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme
  );

  const isComplexItem = metadata || severityRequirement || badge;
  const removeClass = classNames([
    navigationlistRemoveIcon,
    isComplexItem && removeIconPaddingWithEndContent,
    !isComplexItem && endContentPadding
  ]);

  const isLinkItem = href != null;
  const labelRequirement = label && !(label === ' ' && label === undefined);
  const iconOnlyRequirement = icon && icon != null && isIconOnly;
  const showTooltip = iconOnlyRequirement;

  const { tooltipContent, tooltipEventHandlerProps, onLogicalFocus, onLogicalBlur } =
    useTabBarItemTooltip({
      label,
      isDisabled: !showTooltip,
      // For edge end the tooltip will appear after icon in the order tooltip, icon, selection when navlist is placed at end of page.
      position: !isEdgeEnd ? 'end' : 'start'
    });
  if (showTooltip) {
    if ((isCurrent && showFocusRing) || isSelected) {
      onLogicalFocus(itemRef.current as HTMLElement);
    } else if (!isCurrent || !showFocusRing) {
      onLogicalBlur();
    }
  }

  const labelRenderer = () => {
    return (
      <>
        {icon && (
          <Flex justify="start">
            <span class={startIconContainerClasses}>{icon}</span>
          </Flex>
        )}
        {!isIconOnly && labelRequirement && (
          <Flex align="center" justify="start" flex="1 1 auto">
            <span class={labelContainerClasses} style={labelContainerStyle}>
              <Text size="inherit" weight="inherit" variant="inherit">
                {label}
              </Text>
            </span>
          </Flex>
        )}
        {removeIcon && !isLinkItem && <span class={removeClass}>{removeIcon}</span>}
      </>
    );
  };

  const complexItemRenderer = () => {
    return (
      <>
        {iconOnlyRequirement && (
          <div class={startIconContainerClasses}>
            {icon}
            <span class={stackedBadgeClasses}>
              {badge ? (
                <Badge size="xs" variant="neutral">
                  {badge}
                </Badge>
              ) : null}
            </span>
          </div>
        )}
        {!isIconOnly && icon && (
          <Flex justify="start">
            <span class={startIconContainerClasses}>{icon}</span>
          </Flex>
        )}
        {(!isIconOnly || /* This covers invalid cases*/ (isIconOnly && !iconOnlyRequirement)) &&
          labelRequirement && (
            <Flex align="center" justify="start" flex="1 1 auto">
              <span class={labelContainerClasses} style={labelContainerStyle}>
                <Text size="inherit" weight="inherit" variant="inherit">
                  {label}
                </Text>
              </span>
            </Flex>
          )}
        {(metadata || severityRequirement || badge || removeIcon) && (
          <Flex align="center" justify="end">
            {' '}
            {(!isIconOnly || /* This covers invalid cases*/ (isIconOnly && !iconOnlyRequirement)) &&
              badge && (
                <span class={endIconContainerClasses}>
                  <Badge size="md" variant="neutralSubtle">
                    {badge}
                  </Badge>
                </span>
              )}
            {metadata && (
              <span class={navigationItemMatadata}>
                <Text>{'( ' + metadata + ' )'}</Text>
              </span>
            )}
            {severityRequirement && (
              <span class={endIconContainerClasses}>{IconComponent(severity)}</span>
            )}
            {removeIcon && !isLinkItem && <span class={removeClass}>{removeIcon}</span>}
          </Flex>
        )}
      </>
    );
  };
  const navlistItemRenderer = () => {
    return (
      <div
        role="tab"
        {...mergeProps(tooltipEventHandlerProps, itemHandlers)}
        data-oj-key={itemKey}
        id={itemId}
        class={itemClasses}
        aria-selected={isSelected}
        ref={itemRef}
        aria-controls={ariaControls}>
        {isComplexItem ? complexItemRenderer() : labelRenderer()}
        {isIconOnly && tooltipContent}
      </div>
    );
  };
  const navlistLinkItemRenderer = () => {
    return (
      <div {...itemHandlers} class={itemClasses}>
        <a
          tabindex={-1}
          role="tab"
          data-oj-key={itemKey}
          id={itemId}
          class={navigationItemLinkClass}
          aria-selected={isSelected}
          aria-controls={ariaControls}
          href={href}
          onClick={onClickLink}>
          {isComplexItem ? complexItemRenderer() : labelRenderer()}
        </a>
      </div>
    );
  };
  return isLinkItem ? navlistLinkItemRenderer() : navlistItemRenderer();
}
